import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef} from 'react'
import PropTypes from 'prop-types'
import ReCAPTCHA from 'react-google-recaptcha'
import {FormattedMessage} from 'react-intl'
import {Box, Text, useSlotRecipe} from '@chakra-ui/react3'
import Link from '../../leap/components/link'
import useOcapiSite from '../../hooks/useOcapiSite'
import {useNonce} from '../../hooks'
import {recaptchaSlotRecipe} from './index.recipe'

/**
 * A wrapper around the ReCAPTCHA component that handles the site key
 *
 * @param {Object} props
 * @param {'badge'|'text'} [props.display='badge'] - How the reCAPTCHA notice is shown: Google's
 * inline badge, or the attribution text in place of the hidden badge.
 */
const Recaptcha = forwardRef(({display = 'badge'}, ref) => {
    const {reCaptchaPublicKey} = useOcapiSite()
    const styles = useSlotRecipe({recipe: recaptchaSlotRecipe})()
    const innerRef = useRef(null)
    const recaptchaFailed = useRef(false)

    const nonce = useNonce()
    useEffect(() => {
        if (window)
            window.recaptchaOptions = {
                nonce: nonce
            }
    }, [nonce])

    // execute recaptcha and return promise resolving with the token or null if recaptcha is not enabled
    const execute = useCallback(async () => {
        if (!innerRef.current) {
            if (!reCaptchaPublicKey) {
                return null
            }
            throw new Error('Recaptcha not initialized!')
        }
        // fail fast in case recaptcha failed as the call to executeAsync hangs forever
        if (recaptchaFailed.current) {
            throw new Error('Recaptcha failed!')
        }
        return await innerRef.current.executeAsync()
    }, [innerRef, recaptchaFailed, reCaptchaPublicKey])

    // expose the execute function to the parent component
    useImperativeHandle(
        ref,
        () => ({
            execute
        }),
        [execute]
    )

    if (!reCaptchaPublicKey) {
        return null
    }

    const widget = (
        <ReCAPTCHA
            ref={innerRef}
            data-testid="recaptcha"
            sitekey={reCaptchaPublicKey}
            size="invisible"
            badge="inline"
            onErrored={() => (recaptchaFailed.current = true)}
        />
    )

    if (display === 'text') {
        return (
            <>
                <Text css={styles.attribution}>
                    <FormattedMessage
                        id="recaptcha.text.attribution"
                        defaultMessage="This site is protected by reCAPTCHA and the Google <privacy>Privacy Policy</privacy> and <terms>Terms of Service</terms> apply."
                        values={{
                            privacy: (chunks) => (
                                <Link
                                    href="https://policies.google.com/privacy"
                                    isExternal
                                    variant="secondary"
                                    size="xsmall"
                                    underline
                                >
                                    {chunks}
                                </Link>
                            ),
                            terms: (chunks) => (
                                <Link
                                    href="https://policies.google.com/terms"
                                    isExternal
                                    variant="secondary"
                                    size="xsmall"
                                    underline
                                >
                                    {chunks}
                                </Link>
                            )
                        }}
                    />
                </Text>
                <Box css={styles.widget}>{widget}</Box>
            </>
        )
    }

    return widget
})

if (process.env.NODE_ENV !== 'production') {
    Recaptcha.displayName = 'Recaptcha'
}

Recaptcha.propTypes = {
    display: PropTypes.oneOf(['badge', 'text'])
}

export default Recaptcha
